// Emergency visits as a patient source for the billing screens (ADR 0009).
//
// The billing module's patient picker is fed a list of admissions. This fetches
// the emergency visits that have a billing account open against them and shapes
// them like those admissions, so the picker can show both without learning two
// row formats.
//
// Kept out of billing-api.js deliberately: that file is the IPD billing surface
// and is already long, and everything here is about a different subject kind.

import axios from "axios";

import { getBillingHeaders } from "./billing-api";
import { SUBJECT_EMERGENCY } from "./billing-subject";

const API_ROOT = process.env.REACT_APP_BACKEND_ROUTE_URL || "";

/**
 * Emergency visits a billing clerk can act on — those carrying a billing
 * account, for the caller's own facility (the server resolves the facility from
 * the account; it is never sent from here).
 *
 * Returns rows in the shape the picker's admission mapper produces, so
 * `filterAdmissionsByQuery` and the results list work on them unchanged.
 *
 * The differences from an admission row are the honest ones and are left
 * visible rather than faked:
 *   - `admission_id` is null. There is no admission, and inventing one would
 *     put a number on screen that matches nothing in any other system.
 *   - `bed_id` may be blank. A patient waiting on the emergency board has no
 *     bed, which is ordinary rather than missing data.
 *   - `ward_name` is the department, since an emergency visit has no ward.
 */
export async function listEmergencyBillingVisits({ status, search } = {}) {
  const params = {};
  if (status) params.status = status;
  if (search) params.search = search;

  try {
    const res = await axios.get(`${API_ROOT}emergency/billing/visits/`, {
      headers: getBillingHeaders(),
      params,
    });
    if (res.data?.status === "Failed") {
      throw new Error(res.data.message || "Failed to load emergency visits");
    }
    const list = Array.isArray(res.data?.data) ? res.data.data : [];
    return list.map(mapEmergencyVisitForSearch);
  } catch (err) {
    if (err.response?.status === 401) {
      window.location.href = "/login";
    }
    throw new Error(
      err.response?.data?.message || err.message || "Failed to load emergency visits"
    );
  }
}

/** One emergency visit, in the picker's row shape. */
export function mapEmergencyVisitForSearch(row) {
  return {
    subject_kind: SUBJECT_EMERGENCY,
    visit_id: row.visit_id,
    visit_number: row.visit_number || "",
    // Deliberately null rather than the visit id: downstream code treats a
    // present admission_id as an admission, and the two id spaces are unrelated.
    admission_id: null,
    status: row.status,
    admitted_at: row.arrived_at,
    bed_id: row.bed_label || "",
    bed_type_code: "",
    bed_type_name: "",
    department_id: "",
    ward_name: "Emergency",
    doctor_name: row.provider_name || "—",
    patient_id: row.patient_id || "—",
    uhid: row.uhid || row.patient_id || "—",
    patient_name: row.patient_name || "Unidentified patient",
    gender: row.gender,
    payer_type: "",
    payer_label: "",
    billing_account_id: row.billing_account_id,
    billing_encounter_id: row.billing_encounter_id,
  };
}

/**
 * The emergency charge ledger, in the shape the billing charge grid renders.
 *
 * The two sides describe the same events differently: the emergency ledger is a
 * raw audit stream where a reversal is its own CANCELLATION row naming the
 * charge it undoes, while the grid expects one row per charge with a status.
 *
 * CANCELLATION rows are therefore folded away rather than listed — showing both
 * halves would read as two charges — and the charge they reverse is marked so
 * the clerk can see it was pulled back. That keeps the grid agreeing with the
 * ED's own Charges tab about what stands, which is the whole point: two screens
 * disagreeing about what a patient owes is worse than either being sparse.
 *
 * IGNORED charges are dropped, matching what the IPD grid already does.
 */
export function emergencyLedgerToChargeItems(ledger = []) {
  return ledger
    .filter((row) => row.kind !== "CANCELLATION" && !row.ignored)
    .map((row) => ({
      id: row.id,
      charge_event_id: row.id,
      time: row.chargedAt ? new Date(row.chargedAt).toLocaleTimeString("en-IN", {
        hour: "2-digit", minute: "2-digit",
      }) : "—",
      charge_date: row.chargedAt || null,
      code: row.code || "—",
      description: row.name || "—",
      // Tab, group and classification as the server works them out for an
      // admission's charges (label_charges), so both kinds of bill read alike.
      serviceGroup: row.group || "Emergency",
      classification: row.classification || "—",
      medicalService: row.name || "—",
      dept: row.category || "MISC",
      category: row.category || "MISC",
      qty: row.quantity ?? 1,
      rate: row.unitPrice ?? 0,
      amount: row.gross ?? 0,
      source: "manual",
      // A reversed charge says so where the grid shows status, so the two
      // screens cannot disagree about whether it still stands.
      status: row.reversed ? "REVERSED" : (row.pending ? "PENDING" : "PROCESSED"),
      by: "Manual",
      auto: false,
      reversed: Boolean(row.reversed),
      standardUnitPrice: null,
      corporateDiscountAmount: null,
      pricingSource: null,
      pricingApplied: false,
      // Editing and package allocation are ticket 02's concern; nothing here
      // claims a capability the emergency endpoints do not have.
      quantityModifiable: false,
      packagePricingStatus: null,
      packageCode: null,
      remarks: row.remarks || "",
    }));
}

/** The selected-patient context for an emergency visit, mirroring admissionToPatientContext. */
export function emergencyVisitToPatientContext(row) {
  const mapped = row.subject_kind ? row : mapEmergencyVisitForSearch(row);
  return {
    subject_kind: SUBJECT_EMERGENCY,
    visit_id: mapped.visit_id,
    visitId: mapped.visit_id,
    visit_number: mapped.visit_number,
    admission_id: null,
    name: mapped.patient_name,
    patient_id: mapped.patient_id,
    uhid: mapped.uhid || mapped.patient_id,
    bed: mapped.bed_id || "",
    ward: "Emergency",
    bed_type: "",
    bed_type_code: "",
    doctor: mapped.doctor_name,
    gender: mapped.gender,
    payer_type: "",
    payer: "",
    // Deliberately NO `encounter_id`. On an admission context that field holds
    // the admission id and is what the charge endpoints are keyed on, so putting
    // a billing-encounter uuid there would send emergency reads to
    // `ipd_admission/<uuid>/billing/...` — a URL that cannot resolve. Emergency
    // subjects are routed by `visit_id` through billing-subject.js instead.
    billing_encounter_id: mapped.billing_encounter_id || "",
    billing_account_id: mapped.billing_account_id || "",
  };
}

/**
 * Move the ED charges of any Admitted emergency visit that belongs on this
 * admission's bill onto it. Idempotent on the server, so calling it every time
 * the admission is opened is safe. Resolves with how many charges moved and a
 * line per failure ("EV-…: reason"); throws only when the lookup itself fails.
 */
export async function carryOverForAdmission(admissionId) {
  const res = await axios.get(`${API_ROOT}emergency/billing/admission-carryover/`, {
    headers: getBillingHeaders(),
    params: { admission_id: admissionId },
  });
  if (res.data?.status === "Failed") {
    throw new Error(res.data.message || "Could not check for emergency charges");
  }
  const visits = Array.isArray(res.data?.data) ? res.data.data : [];
  let moved = 0;
  const failed = [];
  for (const visit of visits) {
    const r = await axios.post(
      `${API_ROOT}emergency/visits/${visit.visit_id}/billing/transfer-to-admission/`,
      {},
      { headers: getBillingHeaders(true) }
    );
    const outcome = r.data?.data || {};
    moved += outcome.moved || 0;
    (outcome.errors || []).forEach((error) => failed.push(`${visit.visit_number}: ${error}`));
  }
  return { moved, failed };
}

/**
 * Which actions a row of the charge grid offers.
 *
 * A charge that was reversed stands for nothing, so it offers neither edit nor
 * delete (a second reversal would be refused anyway). An emergency charge is
 * reversed from here but never edited: its quantity and price come from the ED,
 * and the emergency endpoints have no edit. An aggregated bed line spans several
 * charges, so single-charge actions do not apply to it.
 */
export function chargeRowActions(row, isEmergency, canAdjust = true) {
  const spent = Boolean(row?.reversed) || String(row?.status || "").toUpperCase() === "REVERSED";
  const single = !row?._aggregated;
  return {
    spent,
    canEdit: single && !spent && !isEmergency,
    // An emergency charge is reversed only by those the server allows to.
    canDelete: single && !spent && (!isEmergency || canAdjust),
  };
}

/** The ED lines under one Charge Capture tab ("ALL" keeps every line). */
export function inCategory(items, category) {
  return !category || category === "ALL" ? items : items.filter((it) => it.category === category);
}
